import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/_shadcn-ui/popover';
import { Bell, X } from 'lucide-react';
import LoadingSpinner2 from '@/components/ui/new-loading-spinner';
import {
  getListNotifications,
  GET_LIST_NOTIFICATIONS_QUERY_KEY,
  TGetListNotificationsResponse,
} from '@/features/notifications/api/get-list-notification';
import { cn } from '@/utils/cn';
import { formatDistanceToNow } from 'date-fns';
import { useIsHasNewNotification, useNotificationActions } from '@/features/notifications/stores/notification.store';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { TNotification } from '@/features/notifications/websocket/use-notification-socket-listeners';

type TApiNotification = TGetListNotificationsResponse['payload']['notifications'][number];

/**
 * Converts strings like "TICKET_UPDATED" or "order-created"
 * into "Ticket Updated" or "Order Created".
 */
export function formatEventName(str: string): string {
  if (!str) return '';

  return str
    .toLowerCase()
    .replace(/[_-]+/g, ' ') // replace underscores or hyphens with space
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function extractPath(url?: string): string {
  if (!url) return '';
  try {
    const urlObj = new URL(url);
    return urlObj.pathname + urlObj.search;
  } catch {
    // If it's already a relative path
    return url.startsWith('/') ? url : '/' + url;
  }
}

const NotificatioPopover = () => {
  // HOOKS
  const navigate = useNavigate();
  const [isOpenPopover, setIsOpenPopover] = useState(false);
  const isHasNewNotification = useIsHasNewNotification();
  const { setIsHasNewNotification } = useNotificationActions();
  // const { actions: ticketingModalActions } = useTicketingModalStore();

  // -- Queries --
  const { data, error, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, status } =
    useInfiniteQuery<TGetListNotificationsResponse>({
      queryKey: [
        GET_LIST_NOTIFICATIONS_QUERY_KEY,
        {
          limit: 10,
        },
      ],
      queryFn: ({ pageParam }): Promise<TGetListNotificationsResponse> =>
        getListNotifications({
          limit: 10,
          cursor: pageParam ? String(pageParam) : null,
        }),
      initialPageParam: null as string | null,
      getNextPageParam: (lastPage) => {
        return lastPage?.payload?.pageInfo?.nextCursor ?? undefined;
      },
      enabled: isOpenPopover,
      staleTime: 0,
      gcTime: 0,
    });

  // Transform infinite query data to flat array of notifications
  const listNotifications = useMemo(() => {
    const pages = (data as InfiniteData<TGetListNotificationsResponse> | undefined)?.pages || [];
    const allNotifications = pages.flatMap((page) => page?.payload?.notifications || []);

    // Deduplicate by notificationId (in case of overlapping data)
    const uniqueNotifications = Array.from(
      new Map(allNotifications.map((notification) => [notification.notificationId, notification])).values()
    );

    // Sort by createdAt descending (newest first)
    return uniqueNotifications.sort((a, b) => {
      return new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime();
    });
  }, [data]);

  const isLoading = status === 'pending' || (isFetching && !data);
  const isError = status === 'error' || !!error;
  const isEmpty = listNotifications.length === 0 && !isLoading;

  const handleClickNotification = useCallback((notification: TApiNotification | TNotification) => {
    // console.log(notification);
    if (!notification.link) return;

    const path = extractPath(notification.link);
    const isTicketingPath = path.startsWith('/ticketing');
    const isActiveCasePath = path.startsWith('/active-cases');
    const isUnassignedCasePath = path.startsWith('/unassigned-cases');
    const isCloseCasePath = path.startsWith('/close-cases');

    if (isTicketingPath) {
      // Extract query params manually
      const url = new URL(path, window.location.origin);
      const spaceId = url.searchParams.get('space');
      // const spaceTab = url.searchParams.get('spaceTab');
      const ticketId = url.searchParams.get('ticket');
      const _spaceColumnId = url.searchParams.get('spaceColumn');

      setIsOpenPopover(false);

      // Do your ticketing logic
      if (ticketId) {
        navigate(`/ticketing?space=${spaceId}&ticket=${ticketId}`);
        window.location.reload();
      }
      return;
    } else if (isActiveCasePath) {
      const url = new URL(path, window.location.origin);
      const caseId = url.searchParams.get('caseId');
      navigate(`/active-cases?caseId=${caseId}`);
      window.location.reload();
      return;
    } else if (isUnassignedCasePath) {
      const url = new URL(path, window.location.origin);
      const caseId = url.searchParams.get('caseId');
      navigate(`/unassigned-cases?caseId=${caseId}`);
      window.location.reload();
      return;
    } else if (isCloseCasePath) {
      const url = new URL(path, window.location.origin);
      const caseId = url.searchParams.get('caseId');
      navigate(`/close-cases?caseId=${caseId}`);
      window.location.reload();
      return;
    }

    // fallback if not ticketing
  }, []);

  // RENDER
  return (
    <Popover open={isOpenPopover} onOpenChange={setIsOpenPopover}>
      <PopoverTrigger>
        <div onClick={() => setIsHasNewNotification(false)} className="relative flex items-center justify-center">
          <Bell className="text-white shrink-0" />
          {isHasNewNotification && (
            <span className="absolute top-0 right-0 w-[10px] h-[10px] bg-[#FF0000] rounded-full " />
          )}
        </div>
      </PopoverTrigger>

      <PopoverContent align="end" className="p-0 w-[320px] overflow-x-hidden">
        {/* Header */}
        <div className="flex justify-center items-center py-[12px] border-b border-[#D9D9D9] relative">
          <h3 className="text-[13px] font-[700] text-[#25151E]">Notification</h3>
          <X
            className="absolute top-[50%] right-[12px] w-[20px] h-[20px] translate-y-[-50%] cursor-pointer"
            onClick={() => setIsOpenPopover(false)}
          />
        </div>

        {/* Content */}
        <div className="flex flex-col  h-[300px] overflow-y-auto bg-[#ffff] overflow-x-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <LoadingSpinner2 className="w-[26px] h-[26px]" />
            </div>
          ) : isError ? (
            <div className="text-center py-4">
              <p className="text-[12px] text-[#9D9D9C]">Error fetching notifications</p>
            </div>
          ) : isEmpty ? (
            <div className="text-center py-4">
              <p className="text-[12px] text-[#9D9D9C]">No notification</p>
            </div>
          ) : (
            <>
              {listNotifications.length > 0 &&
                listNotifications.map((notification, index) => {
                  const isFirstItem = index === 0;
                  const isLastItem = index === listNotifications.length - 1;
                  const isHasLink = notification?.link !== null;

                  return (
                    <div
                      key={notification.notificationId}
                      onClick={() => {
                        handleClickNotification(notification);
                      }}
                      className={cn(
                        'border-b px-[16px] cursor-pointer py-[12px] border-[#D9D9D9]',
                        isFirstItem && '',
                        isLastItem && 'border-none'
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-[12px] font-[700]  text-[#25151E]">
                            {formatEventName(notification.eventName)}
                          </p>
                          <p className="text-[10px] font-[400] mt-[8px] text-[#4B4B4B]">{notification.content}</p>
                          {notification?.createdAt ? (
                            <p className="text-[10px] font-[400] mt-[6px] text-[#9D9D9C]">
                              {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                            </p>
                          ) : null}
                        </div>
                        {isHasLink ? (
                          <svg
                            className="flex-shrink-0"
                            xmlns="http://www.w3.org/2000/svg"
                            width="5"
                            height="9"
                            viewBox="0 0 5 9"
                            fill="none"
                          >
                            <path
                              d="M0.75 7.75L4.25 4.25L0.75 0.75"
                              stroke="#25151E"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : null}
                      </div>
                    </div>
                  );
                })}

              {hasNextPage && (
                <div className="flex items-center justify-center border-t border-[#D9D9D9] p-[12px]">
                  <button
                    className="text-[12px] text-[#1F445A] font-semibold cursor-pointer disabled:opacity-50"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? 'Loading...' : 'Load more'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificatioPopover;
